import type {
    DicomImportResult,
    DicomMetadata,
    ProgressUpdate,
    SeriesInfo,
    WorkerRequest,
    WorkerRequestWithoutId,
    WorkerResponse,
} from './types';

/**
 * Main-thread façade over the DICOM Web Worker.
 *
 * Wraps the bidirectional message protocol in promise-returning methods,
 * correlates responses to their originating request via auto-generated
 * ids, and surfaces progress events through a single subscribe handler.
 *
 * Worker injection is parameterised so tests can stub the worker rather
 * than spinning up a real one.
 */
export class DicomImportPipeline {
    private readonly worker: Worker;
    private readonly pending = new Map<
        string,
        { resolve: (value: WorkerResponse) => void; reject: (error: Error) => void }
    >();
    private progressListeners: Array<(update: ProgressUpdate) => void> = [];
    private nextId = 0;
    private terminated = false;

    constructor(workerFactory: () => Worker = defaultWorkerFactory) {
        this.worker = workerFactory();
        this.worker.addEventListener('message', this.onMessage);
    }

    /** Parse a folder of DICOM files and return the discovered series. */
    async loadFolder(files: File[]): Promise<SeriesInfo[]> {
        const res = await this.send({ type: 'parse-folder', files });
        if (res.type !== 'series-list') {
            throw new Error(`Unexpected response type: ${res.type}`);
        }
        return res.series;
    }

    /** Select one series and return the patient-level DICOM metadata. */
    async selectSeries(seriesInstanceUid: string): Promise<DicomMetadata> {
        const res = await this.send({ type: 'select-series', seriesInstanceUid });
        if (res.type !== 'series-selected') {
            throw new Error(`Unexpected response type: ${res.type}`);
        }
        return res.metadata;
    }

    /** Reconstruct the selected series and produce a DicomImportResult. */
    async reconstruct(): Promise<DicomImportResult> {
        const res = await this.send({ type: 'reconstruct' });
        if (res.type !== 'reconstruct-done') {
            throw new Error(`Unexpected response type: ${res.type}`);
        }
        return res.result;
    }

    /** Subscribe to progress updates. Returns an unsubscribe function. */
    onProgress(listener: (update: ProgressUpdate) => void): () => void {
        this.progressListeners.push(listener);
        return () => {
            this.progressListeners = this.progressListeners.filter((l) => l !== listener);
        };
    }

    /** Terminate the worker and reject any in-flight requests. */
    dispose(): void {
        if (this.terminated) return;
        this.terminated = true;
        this.worker.removeEventListener('message', this.onMessage);
        this.worker.terminate();
        for (const { reject } of this.pending.values()) {
            reject(new Error('DICOM pipeline was disposed.'));
        }
        this.pending.clear();
        this.progressListeners = [];
    }

    private send(request: WorkerRequestWithoutId): Promise<WorkerResponse> {
        if (this.terminated) {
            return Promise.reject(new Error('DICOM pipeline has been disposed.'));
        }
        const id = `req_${this.nextId++}`;
        return new Promise<WorkerResponse>((resolve, reject) => {
            this.pending.set(id, { resolve, reject });
            this.worker.postMessage({ ...request, id } as WorkerRequest);
        });
    }

    private onMessage = (event: MessageEvent<WorkerResponse>) => {
        const response = event.data;
        if (response.type === 'progress') {
            for (const listener of this.progressListeners) listener(response.update);
            return;
        }
        const handler = this.pending.get(response.id);
        if (!handler) return;
        this.pending.delete(response.id);
        if (response.type === 'error') {
            handler.reject(new Error(response.message));
        } else {
            handler.resolve(response);
        }
    };
}

function defaultWorkerFactory(): Worker {
    return new Worker(new URL('./dicom-worker.ts', import.meta.url), { type: 'module' });
}
